import { useState, useEffect, useMemo } from 'react';
import { sharepointAPI, exchangeAPI, businessIntelligenceAPI } from '../lib/api';
import {
  FiCloud, FiMail, FiFolder, FiFolderPlus, FiChevronRight, FiChevronLeft,
  FiCheck, FiLoader, FiX, FiAlertCircle, FiSearch, FiDatabase,
} from 'react-icons/fi';

const ARCHIVE_DOC_TYPES = [
  { value: 'mails', label: 'Mails' },
  { value: 'attachments', label: 'Attachments' },
  { value: 'static_documents', label: 'Static Documents (Read Only)' },
  { value: 'editable_documents', label: 'Editable Documents' },
  { value: 'photos', label: 'Photos' },
  { value: 'images', label: 'Images' },
];

/**
 * ArchiveFolderBrowser
 *
 * Multi-step wizard restricted to already-connected SharePoint sites/drives and Exchange mailboxes.
 * Steps: Source → Site (connected only) → Drive (connected only) → Folder (deep drill) → Type
 *
 * @param {function} onAdd - called with a complete FolderMapping object
 * @param {function} onCancel - called when user cancels
 * @param {Array} customDocTypes - custom doc types for this group [{ id, name }]
 */
export default function ArchiveFolderBrowser({ onAdd, onCancel, destinationMode = false, customDocTypes = [] }) {
  const STEPS = { SOURCE: 0, SITE: 1, DRIVE: 2, FOLDER: 3, TYPE: 4 };

  const [step, setStep] = useState(STEPS.SOURCE);
  const [source, setSource] = useState(null); // 'sharepoint' | 'exchange' | 'doxolve'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // SharePoint: the one active connection + its connected sites/drives
  const [spConnection, setSpConnection] = useState(null);
  const [connectedSites, setConnectedSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteSearch, setSiteSearch] = useState('');
  const [connectedDrives, setConnectedDrives] = useState([]);
  const [selectedDrive, setSelectedDrive] = useState(null);
  const [driveSearch, setDriveSearch] = useState('');

  // Exchange: connected mailboxes
  const [exConnection, setExConnection] = useState(null);
  const [mailboxes, setMailboxes] = useState([]);
  const [selectedMailbox, setSelectedMailbox] = useState(null);
  const [mailboxSearch, setMailboxSearch] = useState('');

  // Folder drill-down (shared for SP + Exchange + Doxolve)
  const [folderStack, setFolderStack] = useState([]);
  const [currentFolders, setCurrentFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderSearch, setFolderSearch] = useState('');

  // Type selection — multi-select array
  const [selectedTypes, setSelectedTypes] = useState([]);

  // All available types: predefined + custom
  const allDocTypes = useMemo(() => [
    ...ARCHIVE_DOC_TYPES,
    ...customDocTypes.map(t => ({ value: t.id, label: t.name })),
  ], [customDocTypes]);

  const toggleType = (value) => {
    setSelectedTypes(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  };

  // ─── Source selection ──────────────────────────────────────────────────────

  const handleSourceSelect = async (src) => {
    setSource(src);
    setError(null);
    setLoading(true);
    try {
      if (src === 'doxolve') {
        await loadDoxolveFolders(null);
        setStep(STEPS.FOLDER);
        setLoading(false);
        return;
      }
      if (src === 'sharepoint') {
        const res = await sharepointAPI.getConnections();
        const active = (res.data.connections || []).filter(c => c.status === 'active');
        if (active.length === 0) {
          setError('No active SharePoint connections found. Connect one under Integrations first.');
          setLoading(false);
          return;
        }
        const conn = active[0];
        setSpConnection(conn);

        const raw = conn.connected_sites || [];
        const siteMap = {};
        raw.forEach(s => {
          if (!siteMap[s.site_id]) siteMap[s.site_id] = { site_id: s.site_id, site_name: s.site_name };
        });
        const sites = Object.values(siteMap);

        if (sites.length === 0) {
          setError('No connected sites found. Use Integrations → SharePoint → Manage to select sites first.');
          setLoading(false);
          return;
        }
        setConnectedSites(sites);
        setSiteSearch('');
        setStep(STEPS.SITE);
      } else {
        const res = await exchangeAPI.getConnections();
        const active = (res.data.connections || []).filter(c => c.status === 'active');
        if (active.length === 0) {
          setError('No active Exchange connections found. Connect one under Integrations first.');
          setLoading(false);
          return;
        }
        const conn = active[0];
        setExConnection(conn);
        const mbs = conn.connected_mailboxes || [];
        if (mbs.length === 0) {
          setError('No mailboxes configured. Use Integrations → Exchange → Manage to add mailboxes first.');
          setLoading(false);
          return;
        }
        setMailboxes(mbs);
        setMailboxSearch('');
        setStep(STEPS.SITE);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── Site / Mailbox selection ─────────────────────────────────────────────

  const handleSiteSelect = (site) => {
    setSelectedSite(site);
    setDriveSearch('');

    const raw = spConnection?.connected_sites || [];
    const drives = raw
      .filter(s => s.site_id === site.site_id)
      .map(s => ({ drive_id: s.drive_id, drive_name: s.drive_name }))
      .filter((d, i, arr) => arr.findIndex(x => x.drive_id === d.drive_id) === i);

    setConnectedDrives(drives);
    setSelectedDrive(null);
    setStep(STEPS.DRIVE);
  };

  const handleMailboxSelect = async (mb) => {
    setSelectedMailbox(mb);
    setFolderStack([]);
    setSelectedFolder(null);
    setFolderSearch('');
    setError(null);
    await loadExchangeFolders(mb, null);
    setStep(STEPS.FOLDER);
  };

  // ─── Drive selection ──────────────────────────────────────────────────────

  const handleDriveSelect = async (drive) => {
    setSelectedDrive(drive);
    setFolderStack([]);
    setSelectedFolder(null);
    setFolderSearch('');
    setError(null);
    await loadSharePointFolders(drive, null);
    setStep(STEPS.FOLDER);
  };

  // ─── Folder browsing ──────────────────────────────────────────────────────

  const loadSharePointFolders = async (drive, folderId) => {
    const driveToUse = drive || selectedDrive;
    setLoading(true);
    setError(null);
    try {
      const res = await businessIntelligenceAPI.getSharePointFolders(
        spConnection.id,
        driveToUse.drive_id,
        folderId
      );
      setCurrentFolders(res.data.folders || []);
      setFolderSearch('');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadExchangeFolders = async (mb, folderId) => {
    const mbToUse = mb || selectedMailbox;
    setLoading(true);
    setError(null);
    try {
      const res = await businessIntelligenceAPI.getMailFolders(
        exConnection.id,
        mbToUse.user_id,
        folderId
      );
      setCurrentFolders(res.data.folders || []);
      setFolderSearch('');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDoxolveFolders = async (folderId) => {
    setLoading(true);
    setError(null);
    try {
      const res = await businessIntelligenceAPI.getDoxolveFolders(folderId);
      const folders = (res.data.folders || []).map(f => ({
        ...f,
        childCount: 1,
      }));
      setCurrentFolders(folders);
      setFolderSearch('');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFolderDrillDown = async (folder) => {
    const hasChildren = source === 'sharepoint'
      ? folder.childCount > 0
      : source === 'doxolve'
        ? true
        : folder.childFolderCount > 0;
    if (!hasChildren) return;
    setFolderStack(prev => [...prev, { id: folder.id, name: folder.name || folder.displayName }]);
    setSelectedFolder(null);
    if (source === 'sharepoint') {
      await loadSharePointFolders(null, folder.id);
    } else if (source === 'doxolve') {
      await loadDoxolveFolders(folder.id);
    } else {
      await loadExchangeFolders(null, folder.id);
    }
  };

  const handleFolderBack = async () => {
    const newStack = folderStack.slice(0, -1);
    setFolderStack(newStack);
    setSelectedFolder(null);
    const parentId = newStack.length > 0 ? newStack[newStack.length - 1].id : null;
    if (source === 'sharepoint') {
      await loadSharePointFolders(null, parentId);
    } else if (source === 'doxolve') {
      await loadDoxolveFolders(parentId);
    } else {
      await loadExchangeFolders(null, parentId);
    }
  };

  // ─── Path builders ────────────────────────────────────────────────────────

  const buildSharePointPath = () => {
    const parts = [selectedSite?.site_name, selectedDrive?.drive_name, ...folderStack.map(f => f.name)];
    if (selectedFolder) parts.push(selectedFolder.name);
    return parts.filter(Boolean).join(' / ');
  };

  const buildExchangePath = () => {
    const parts = [selectedMailbox?.email, ...folderStack.map(f => f.name)];
    if (selectedFolder) parts.push(selectedFolder.displayName || selectedFolder.name);
    return parts.filter(Boolean).join(' / ');
  };

  const buildDoxolvePath = () => {
    const parts = [...folderStack.map(f => f.name)];
    if (selectedFolder) parts.push(selectedFolder.name);
    return parts.filter(Boolean).join(' / ') || 'Root';
  };

  // ─── Final confirm ────────────────────────────────────────────────────────

  const handleConfirm = (overrideType = null) => {
    if (!destinationMode && selectedTypes.length === 0 && !overrideType) return;

    const mapping = {
      id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
      source,
      // keep backward-compat: type = first selected, types = full array
      type: overrideType || selectedTypes[0] || null,
      types: overrideType ? [overrideType] : selectedTypes,
    };

    if (source === 'sharepoint') {
      const activeFolderId = selectedFolder?.id ?? (folderStack.length > 0 ? folderStack[folderStack.length - 1].id : null);
      const activeFolderName = selectedFolder?.name ?? (folderStack.length > 0 ? folderStack[folderStack.length - 1].name : selectedDrive.drive_name);
      mapping.connection_id = spConnection.id;
      mapping.drive_id = selectedDrive.drive_id;
      mapping.drive_name = selectedDrive.drive_name;
      mapping.site_name = selectedSite.site_name;
      mapping.folder_id = activeFolderId;
      mapping.folder_name = activeFolderName;
      mapping.folder_path = buildSharePointPath();
    } else if (source === 'doxolve') {
      const activeFolderId = selectedFolder?.id ?? (folderStack.length > 0 ? folderStack[folderStack.length - 1].id : null);
      const activeFolderName = selectedFolder?.name ?? (folderStack.length > 0 ? folderStack[folderStack.length - 1].name : 'Root');
      mapping.folder_id = activeFolderId;
      mapping.folder_name = activeFolderName;
      mapping.folder_path = buildDoxolvePath();
    } else {
      const activeFolderId = selectedFolder?.id ?? (folderStack.length > 0 ? folderStack[folderStack.length - 1].id : null);
      const activeFolderName = selectedFolder ? (selectedFolder.displayName || selectedFolder.name) : (folderStack.length > 0 ? folderStack[folderStack.length - 1].name : 'Inbox');
      mapping.connection_id = exConnection.id;
      mapping.mailbox_id = selectedMailbox.user_id;
      mapping.mailbox_email = selectedMailbox.email;
      mapping.mail_folder_id = activeFolderId;
      mapping.mail_folder_name = activeFolderName;
    }

    onAdd(mapping);
  };

  // ─── Filtered lists ────────────────────────────────────────────────────────

  const filteredSites = useMemo(() => {
    const q = siteSearch.toLowerCase();
    return q ? connectedSites.filter(s => s.site_name?.toLowerCase().includes(q)) : connectedSites;
  }, [connectedSites, siteSearch]);

  const filteredDrives = useMemo(() => {
    const q = driveSearch.toLowerCase();
    return q ? connectedDrives.filter(d => d.drive_name?.toLowerCase().includes(q)) : connectedDrives;
  }, [connectedDrives, driveSearch]);

  const filteredMailboxes = useMemo(() => {
    const q = mailboxSearch.toLowerCase();
    return q ? mailboxes.filter(m => (m.display_name || '').toLowerCase().includes(q) || m.email?.toLowerCase().includes(q)) : mailboxes;
  }, [mailboxes, mailboxSearch]);

  const filteredFolders = useMemo(() => {
    const q = folderSearch.toLowerCase();
    return q ? currentFolders.filter(f => (f.name || f.displayName || '').toLowerCase().includes(q)) : currentFolders;
  }, [currentFolders, folderSearch]);

  // ─── Navigation helpers ───────────────────────────────────────────────────

  const handleBack = () => {
    setError(null);
    if (step === STEPS.SITE) { setStep(STEPS.SOURCE); setSource(null); }
    else if (step === STEPS.DRIVE) { setStep(STEPS.SITE); setConnectedDrives([]); setSelectedSite(null); }
    else if (step === STEPS.FOLDER && folderStack.length > 0) { handleFolderBack(); }
    else if (step === STEPS.FOLDER) {
      setCurrentFolders([]);
      if (source === 'sharepoint') setStep(STEPS.DRIVE);
      else if (source === 'doxolve') { setStep(STEPS.SOURCE); setSource(null); }
      else setStep(STEPS.SITE);
    }
    else if (step === STEPS.TYPE) { setStep(STEPS.FOLDER); }
  };

  // ─── Breadcrumb ───────────────────────────────────────────────────────────

  const renderBreadcrumb = () => {
    if (step < STEPS.FOLDER) return null;
    const crumbs = source === 'sharepoint'
      ? [selectedSite?.site_name, selectedDrive?.drive_name, ...folderStack.map(f => f.name)].filter(Boolean)
      : source === 'doxolve'
        ? ['Doxolve', ...folderStack.map(f => f.name)].filter(Boolean)
        : [selectedMailbox?.email, ...folderStack.map(f => f.name)].filter(Boolean);

    return (
      <div className="flex items-center flex-wrap gap-1 text-xs bg-gray-50 rounded px-3 py-2 min-h-[2rem]">
        {crumbs.map((label, i) => (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && <FiChevronRight className="w-3 h-3 text-gray-400" />}
            <span className={i === crumbs.length - 1 ? 'font-semibold text-gray-700' : 'text-gray-500'}>{label}</span>
          </span>
        ))}
        {selectedFolder && (
          <span className="flex items-center gap-1">
            <FiChevronRight className="w-3 h-3 text-gray-400" />
            <span className="font-semibold text-blue-600">{selectedFolder.name || selectedFolder.displayName}</span>
          </span>
        )}
      </div>
    );
  };

  // ─── Search box ───────────────────────────────────────────────────────────

  const SearchBox = ({ value, onChange, placeholder }) => (
    <div className="relative mb-2">
      <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || 'Search…'}
        className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-400 focus:border-transparent bg-white"
      />
      {value && (
        <button onClick={() => onChange('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
          <FiX className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );

  // ─── Step renderers ───────────────────────────────────────────────────────

  const renderSource = () => (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        {destinationMode ? 'Choose the SharePoint folder where accepted documents will be copied:' : 'Choose the data source for this folder:'}
      </p>
      <div className={`grid gap-4 ${destinationMode ? 'grid-cols-1 max-w-xs' : 'grid-cols-3'}`}>
        <button
          onClick={() => handleSourceSelect('sharepoint')}
          disabled={loading}
          className="flex flex-col items-center gap-3 p-6 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors disabled:opacity-50"
        >
          <FiCloud className="w-10 h-10 text-blue-600" />
          <div>
            <p className="font-medium text-gray-900">SharePoint</p>
            <p className="text-xs text-gray-500 mt-0.5">Connected sites & libraries</p>
          </div>
        </button>
        {!destinationMode && (
          <>
            <button
              onClick={() => handleSourceSelect('exchange')}
              disabled={loading}
              className="flex flex-col items-center gap-3 p-6 border-2 border-gray-200 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors disabled:opacity-50"
            >
              <FiMail className="w-10 h-10 text-purple-600" />
              <div>
                <p className="font-medium text-gray-900">Exchange</p>
                <p className="text-xs text-gray-500 mt-0.5">Connected mailboxes</p>
              </div>
            </button>
            <button
              onClick={() => handleSourceSelect('doxolve')}
              disabled={loading}
              className="flex flex-col items-center gap-3 p-6 border-2 border-gray-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-colors disabled:opacity-50"
            >
              <FiDatabase className="w-10 h-10 text-green-600" />
              <div>
                <p className="font-medium text-gray-900">Doxolve</p>
                <p className="text-xs text-gray-500 mt-0.5">Local document folders</p>
              </div>
            </button>
          </>
        )}
      </div>
      {loading && (
        <div className="flex items-center justify-center gap-2 py-4">
          <FiLoader className="w-5 h-5 animate-spin text-gray-400" />
          <span className="text-sm text-gray-500">Loading…</span>
        </div>
      )}
    </div>
  );

  const renderSite = () => {
    if (source === 'sharepoint') {
      return (
        <div className="space-y-2">
          <p className="text-sm text-gray-600">
            Select a connected site ({connectedSites.length} available):
          </p>
          <SearchBox value={siteSearch} onChange={setSiteSearch} placeholder="Search sites…" />
          {filteredSites.length === 0 ? (
            <div className="text-center py-6 text-gray-400 text-sm border border-dashed border-gray-200 rounded-lg">
              No sites match "{siteSearch}"
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden max-h-72 overflow-y-auto">
              {filteredSites.map(site => (
                <li
                  key={site.site_id}
                  onClick={() => handleSiteSelect(site)}
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-blue-50 transition-colors group"
                >
                  <FiCloud className="w-4 h-4 text-blue-400 flex-shrink-0" />
                  <span className="flex-1 text-sm text-gray-800 truncate">{site.site_name}</span>
                  <FiChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-400 flex-shrink-0" />
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <p className="text-sm text-gray-600">
          Select a connected mailbox ({mailboxes.length} available):
        </p>
        <SearchBox value={mailboxSearch} onChange={setMailboxSearch} placeholder="Search mailboxes…" />
        {filteredMailboxes.length === 0 ? (
          <div className="text-center py-6 text-gray-400 text-sm border border-dashed border-gray-200 rounded-lg">
            No mailboxes match "{mailboxSearch}"
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden max-h-72 overflow-y-auto">
            {filteredMailboxes.map(mb => (
              <li
                key={mb.user_id}
                onClick={() => handleMailboxSelect(mb)}
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-purple-50 transition-colors group"
              >
                <FiMail className="w-4 h-4 text-purple-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{mb.display_name || mb.email}</p>
                  <p className="text-xs text-gray-400 truncate">{mb.email}</p>
                </div>
                <FiChevronRight className="w-4 h-4 text-gray-300 group-hover:text-purple-400 flex-shrink-0" />
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const renderDrive = () => (
    <div className="space-y-2">
      <p className="text-sm text-gray-600">
        Select a document library in <span className="font-medium">{selectedSite?.site_name}</span>:
      </p>
      <SearchBox value={driveSearch} onChange={setDriveSearch} placeholder="Search libraries…" />
      {filteredDrives.length === 0 ? (
        <div className="text-center py-6 text-gray-400 text-sm border border-dashed border-gray-200 rounded-lg">
          {driveSearch ? `No libraries match "${driveSearch}"` : 'No connected libraries for this site'}
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden max-h-72 overflow-y-auto">
          {filteredDrives.map(drive => (
            <li
              key={drive.drive_id}
              onClick={() => handleDriveSelect(drive)}
              className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-yellow-50 transition-colors group"
            >
              <FiFolder className="w-4 h-4 text-yellow-500 flex-shrink-0" />
              <span className="flex-1 text-sm text-gray-800 truncate">{drive.drive_name}</span>
              <FiChevronRight className="w-4 h-4 text-gray-300 group-hover:text-yellow-500 flex-shrink-0" />
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const renderFolder = () => {
    const isRoot = folderStack.length === 0;
    const currentLocationLabel = isRoot
      ? (source === 'sharepoint' ? selectedDrive?.drive_name : source === 'doxolve' ? 'Doxolve Root' : 'Inbox')
      : folderStack[folderStack.length - 1].name;

    return (
      <div className="space-y-2">
        {renderBreadcrumb()}

        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            {loading ? 'Loading folders…' : `${currentFolders.length} subfolder${currentFolders.length !== 1 ? 's' : ''} in ${currentLocationLabel}`}
          </p>
          {!isRoot && (
            <button
              onClick={handleFolderBack}
              disabled={loading}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors disabled:opacity-50"
            >
              <FiChevronLeft className="w-3.5 h-3.5" />
              Up one level
            </button>
          )}
        </div>

        <SearchBox value={folderSearch} onChange={setFolderSearch} placeholder="Search folders…" />

        <div
          onClick={() => setSelectedFolder(null)}
          className={`flex items-center gap-3 px-3 py-2.5 border-2 rounded-lg cursor-pointer transition-colors ${
            selectedFolder === null ? 'border-blue-400 bg-blue-50' : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
          }`}
        >
          <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
            selectedFolder === null ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
          }`}>
            {selectedFolder === null && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900">Use this location</p>
            <p className="text-xs text-gray-400 truncate">{currentLocationLabel}</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-6">
            <FiLoader className="w-5 h-5 animate-spin text-gray-400" />
            <span className="text-sm text-gray-500">Loading folders…</span>
          </div>
        ) : filteredFolders.length === 0 && !folderSearch ? (
          <div className="text-center py-4 text-gray-400 text-sm border border-dashed border-gray-200 rounded-lg">
            No subfolders — use this location or go back
          </div>
        ) : filteredFolders.length === 0 ? (
          <div className="text-center py-4 text-gray-400 text-sm border border-dashed border-gray-200 rounded-lg">
            No folders match "{folderSearch}"
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden max-h-52 overflow-y-auto">
            {filteredFolders.map(folder => {
              const fName = folder.name || folder.displayName;
              const hasChildren = source === 'sharepoint' ? folder.childCount > 0 : source === 'doxolve' ? true : folder.childFolderCount > 0;
              const isSelected = selectedFolder?.id === folder.id;

              return (
                <li
                  key={folder.id}
                  className={`flex items-center gap-3 px-3 py-2.5 transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                >
                  <div
                    onClick={() => setSelectedFolder(isSelected ? null : folder)}
                    className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center cursor-pointer ${
                      isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300 hover:border-blue-400'
                    }`}
                  >
                    {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                  <FiFolder
                    className="w-4 h-4 text-yellow-500 flex-shrink-0 cursor-pointer"
                    onClick={() => setSelectedFolder(isSelected ? null : folder)}
                  />
                  <span
                    className="flex-1 text-sm text-gray-800 truncate cursor-pointer"
                    onClick={() => setSelectedFolder(isSelected ? null : folder)}
                  >
                    {fName}
                  </span>
                  {hasChildren && (
                    <button
                      onClick={() => handleFolderDrillDown(folder)}
                      className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors flex-shrink-0"
                      title={`Open ${fName}`}
                    >
                      <FiChevronRight className="w-4 h-4" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <p className="text-xs text-gray-400 pt-1">
          Select a radio button to use that folder as the destination. Click <FiChevronRight className="inline w-3 h-3" /> to drill into subfolders.
        </p>
      </div>
    );
  };

  const renderType = () => {
    const path = source === 'sharepoint' ? buildSharePointPath()
      : source === 'doxolve' ? buildDoxolvePath()
      : buildExchangePath();
    return (
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs text-gray-500 mb-1">Selected folder:</p>
          <p className="text-sm font-medium text-gray-800 break-all">{path}</p>
        </div>
        <p className="text-sm text-gray-600">Select one or more document types for this folder:</p>
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden max-h-72 overflow-y-auto">
          {allDocTypes.map(dt => {
            const isChecked = selectedTypes.includes(dt.value);
            const isCustom = customDocTypes.some(c => c.id === dt.value);
            return (
              <li
                key={dt.value}
                onClick={() => toggleType(dt.value)}
                className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                  isChecked ? 'bg-green-50' : 'hover:bg-gray-50'
                }`}
              >
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                  isChecked ? 'border-green-500 bg-green-500' : 'border-gray-300'
                }`}>
                  {isChecked && <FiCheck className="w-2.5 h-2.5 text-white" />}
                </div>
                <span className="flex-1 text-sm text-gray-800">{dt.label}</span>
                {isCustom && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-600 font-medium">custom</span>
                )}
              </li>
            );
          })}
        </ul>
        {selectedTypes.length > 0 && (
          <p className="text-xs text-green-700 font-medium">
            {selectedTypes.length} type{selectedTypes.length !== 1 ? 's' : ''} selected
          </p>
        )}
      </div>
    );
  };

  // ─── Step metadata ────────────────────────────────────────────────────────

  const stepTitles = [
    'Choose Source',
    source === 'sharepoint' ? 'Select Connected Site' : 'Select Mailbox',
    'Select Document Library',
    'Select Folder',
    'Assign Document Types',
  ];

  const effectiveStepCount = source === 'sharepoint' ? 5 : source === 'doxolve' ? 3 : 4;
  const effectiveStepIndex = source === 'exchange' && step >= STEPS.FOLDER
    ? step - 1
    : source === 'doxolve' && step >= STEPS.FOLDER
      ? step - 2
      : step;

  const canGoBack = step > STEPS.SOURCE;
  const canNext = step === STEPS.FOLDER && !destinationMode;
  const canConfirmDestination = step === STEPS.FOLDER && destinationMode && source === 'sharepoint';
  const canConfirm = step === STEPS.TYPE && selectedTypes.length > 0;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FiFolderPlus className="w-5 h-5 text-green-600" />
            {destinationMode ? 'Set Archive Destination' : 'Add Folder'}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">{stepTitles[step]}</p>
        </div>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 transition-colors">
          <FiX className="w-5 h-5" />
        </button>
      </div>

      <div className="flex items-center gap-1">
        {Array.from({ length: effectiveStepCount }).map((_, i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors ${
              i <= effectiveStepIndex ? 'bg-green-500' : 'bg-gray-200'
            }`}
          />
        ))}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <FiAlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="min-h-[220px]">
        {step === STEPS.SOURCE && renderSource()}
        {step === STEPS.SITE && renderSite()}
        {step === STEPS.DRIVE && source === 'sharepoint' && renderDrive()}
        {step === STEPS.FOLDER && renderFolder()}
        {step === STEPS.TYPE && renderType()}
      </div>

      <div className="flex justify-between items-center pt-2 border-t border-gray-200">
        <button
          onClick={canGoBack ? handleBack : onCancel}
          disabled={loading}
          className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          <FiChevronLeft className="w-4 h-4" />
          {canGoBack ? 'Back' : 'Cancel'}
        </button>

        {canNext && (
          <button
            onClick={() => setStep(STEPS.TYPE)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Next: Assign Types
            <FiChevronRight className="w-4 h-4" />
          </button>
        )}

        {canConfirmDestination && (
          <button
            onClick={() => handleConfirm('_destination')}
            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FiCheck className="w-4 h-4" />
            Set as Destination
          </button>
        )}

        {canConfirm && (
          <button
            onClick={() => handleConfirm()}
            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <FiCheck className="w-4 h-4" />
            Add Folder Mapping
          </button>
        )}
      </div>
    </div>
  );
}
